import { FileText, Download, Upload, Loader2, Eye, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState, useRef, useEffect } from "react";
import { FileUploadTrigger } from "@/components/native/FileUploadTrigger";
import { VehicleTest } from "@/types/vehicleTest";
import { PaymentWithDetails, Invoice, Contract } from "@/types/payment";
import { FileObject } from "@/types/shared/file";
import { generateVehicleTestPdf } from "@/utils/pdfGenerator";
import { useLanguage } from "@/context/LanguageContext";
import { carTestLabels, vehicleCardUILabels } from "@/i18n";
import { VehicleTestDocumentView } from "@/admin/pages/VehicleTestDocumentView";
import { VehicleCardTestSectionCard } from "@/admin/car-card/VehicleCardTestSectionCard";
import { useVehicleCard } from "@/hooks/useVehicleCard";
import { InvoiceUploadDialog } from "@/components/dialogs/InvoiceUploadDialog";
import { ContractUploadDialog } from "@/components/dialogs/ContractUploadDialog";
import { useFile } from "@/hooks/useFile";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PdfGenerationDialog } from "@/components/ui/PdfGenerationDialog";

// Categories – labels from i18n (ownership, test, otherDocuments)
const getCategories = (language: "sv" | "en") => {
  const vc = vehicleCardUILabels(language);
  return [
    { id: "ownership", label: vc.ownership },
    { id: "test", label: vc.test },
    { id: "other", label: vc.otherDocuments },
  ];
};

interface VehicleCardDocumentsProps {
  regNumber?: string;
  vehicleTests?: VehicleTest[];
  payment?: PaymentWithDetails;
  owner_change?: FileObject[];
  other_documents?: FileObject[];
  vehicleCardId: string; // Required: always available in VehicleCard context
}

export const VehicleCardDocuments = ({
  regNumber,
  vehicleTests,
  payment,
  owner_change,
  other_documents,
  vehicleCardId,
}: VehicleCardDocumentsProps) => {
  const { language } = useLanguage();

  const { downloadFile } = useFile();
  const {
    addVehicleCardDocument,
    removeDocument,
    isAddingVehicleCardDocument,
    isRemovingDocument,
  } = useVehicleCard();

  // Local state for payment (contract/invoice) to enable optimistic updates
  const [currentPayment, setCurrentPayment] = useState<
    PaymentWithDetails | undefined
  >(payment);
  const [currentOwnerChange, setCurrentOwnerChange] = useState<FileObject[]>(
    owner_change || [],
  );
  const [currentOtherDocuments, setCurrentOtherDocuments] = useState<
    FileObject[]
  >(other_documents || []);

  // Sync local state with props when they change
  //
  // Why these are needed:
  // 1. For optimistic updates (file input uploads): Local state is updated immediately before
  //    the mutation, providing instant UI feedback. If mutation fails, onError callbacks
  //    rollback the optimistic update. When mutation succeeds, onSuccess updates state with
  //    server response, and React Query refetch also syncs via these effects.
  // 2. For dialog uploads (invoice/contract): When createInvoiceAsync/createContractAsync
  //    succeeds, onSuccess callback immediately updates local state with the returned
  //    Invoice/Contract object (no need to wait for React Query refetch). If operation fails,
  //    props don't update (no query invalidation) and local state remains correct (no rollback
  //    needed since we only update on success).
  // 3. For external updates: When data changes from other sources (other windows, other users),
  //    props will update and we need to sync local state
  //
  // Note: For file input uploads, we update state optimistically (before mutation). For
  // dialog uploads, we update state immediately after success (with returned data).
  useEffect(() => {
    if (
      payment &&
      (!currentPayment ||
        currentPayment.id !== payment.id ||
        currentPayment.contract?.contract_file?.id !==
          payment.contract?.contract_file?.id ||
        currentPayment.invoice?.invoice_file?.id !==
          payment.invoice?.invoice_file?.id)
    ) {
      setCurrentPayment(payment);
    }
  }, [payment, currentPayment]);

  useEffect(() => {
    if (owner_change) {
      const currentIds = new Set(currentOwnerChange.map((d) => d.id));
      const newIds = new Set(owner_change.map((d) => d.id));
      if (
        currentIds.size !== newIds.size ||
        [...currentIds].some((id) => !newIds.has(id)) ||
        [...newIds].some((id) => !currentIds.has(id))
      ) {
        setCurrentOwnerChange(owner_change);
      }
    }
  }, [owner_change, currentOwnerChange]);

  useEffect(() => {
    if (other_documents) {
      const currentIds = new Set(currentOtherDocuments.map((d) => d.id));
      const newIds = new Set(other_documents.map((d) => d.id));
      if (
        currentIds.size !== newIds.size ||
        [...currentIds].some((id) => !newIds.has(id)) ||
        [...newIds].some((id) => !currentIds.has(id))
      ) {
        setCurrentOtherDocuments(other_documents);
      }
    }
  }, [other_documents, currentOtherDocuments]);

  // Handle both single test object and array of tests
  const testData = vehicleTests
    ? Array.isArray(vehicleTests)
      ? vehicleTests
      : [vehicleTests]
    : [];
  // Only show contracts/invoices that have a file attached
  const contractData = currentPayment?.contract?.contract_file
    ? [currentPayment.contract]
    : [];
  const invoiceData = currentPayment?.invoice?.invoice_file
    ? [currentPayment.invoice]
    : [];

  const [selectedCategory, setSelectedCategory] = useState<string>("ownership");
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<{
    isOpen: boolean;
    testName?: string;
    fileName?: string;
  }>({
    isOpen: false,
  });
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const openDocumentPickerRef = useRef<(() => void) | null>(null);

  // State for test viewing and downloading
  const [viewTest, setViewTest] = useState<VehicleTest | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  // Check if a specific test is generating PDF
  const isTestGeneratingPdf = (testId: string) => false;

  // Handle test viewing
  const handleViewTest = (test: VehicleTest) => {
    setViewTest(test);
    setIsViewDialogOpen(true);
  };

  // Handle test PDF download – professionell PDF (BILENIA-header, sidnummer, alla sektioner + bilder)
  const handleDownloadTestPdf = async (test: VehicleTest) => {
    setIsGeneratingPdf({
      isOpen: true,
      testName: test.vehicleName,
      fileName: `fordonsprotokoll-${test.vehicleDetails?.regNumber || test.id}.pdf`,
    });
    await new Promise((r) => setTimeout(r, 100));
    try {
      await generateVehicleTestPdf(test, language);
      const ct = carTestLabels(language);
      toast.success(ct.messages.pdfGenerated || "PDF genererad", {
        description: ct.messages.pdfGenerated ||
          "Testprotokollet har laddats ner som PDF.",
      });
    } catch (error) {
      toast.error("Fel vid PDF-generering", { description: "Något gick fel när PDF:en skulle genereras." });
    } finally {
      await new Promise((r) => setTimeout(r, 300));
      setIsGeneratingPdf({ isOpen: false });
    }
  };

  // Handle document download from URL
  const handleDownloadDocument = async (document: {
    id: string;
    url: string;
    name?: string;
  }) => {
    try {
      const filename = await downloadFile(
        document.url,
        document.name || "document.pdf",
      );
      toast.success(vehicleCardUILabels(language).downloadStarted, {
        description: vehicleCardUILabels(language).downloadStartedDesc.replace(
          "{name}",
          filename,
        ),
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Okänt fel";
      toast.error(vehicleCardUILabels(language).downloadFailed, { description: `${document.name || "filen"}\nFel: ${errorMessage}` });
    }
  };

  const handleUploadDocument = (category: string) => {
    setSelectedCategory(category);

    if (category === "contract") {
      // Check if contract already exists
      if (currentPayment?.contract?.contract_file) {
        setWarningMessage(vehicleCardUILabels(language).warningOneContract);
        setWarningDialogOpen(true);
        return;
      }

      setContractDialogOpen(true);
    } else if (category === "invoice") {
      // Check if invoice already exists
      if (currentPayment?.invoice?.invoice_file) {
        setWarningMessage(vehicleCardUILabels(language).warningOneInvoice);
        setWarningDialogOpen(true);
        return;
      }

      setInvoiceDialogOpen(true);
    } else {
      // For ownership and other documents, use file input (no validation needed - can have multiple)
      openDocumentPickerRef.current?.();
    }
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];

      // Reset handled by picker (no persistent input)

      // Map selectedCategory to document category
      // Note: contract and invoice use their own dialogs, so this only handles ownership and other
      let category: "ownerChange" | "other";
      if (selectedCategory === "ownership") {
        category = "ownerChange";
      } else if (selectedCategory === "other") {
        category = "other";
      } else {
        toast.error(vehicleCardUILabels(language).unknownError, { description: vehicleCardUILabels(language).invalidCategory });
        return;
      }

      // Optimistically add document to local state immediately
      const newFileObject: FileObject = {
        id: `temp_${Date.now()}`,
        url: URL.createObjectURL(file),
        name: file.name,
        type: file.type,
        size: file.size,
      };

      if (category === "ownerChange") {
        setCurrentOwnerChange((prev) => [...prev, newFileObject]);
      } else if (category === "other") {
        setCurrentOtherDocuments((prev) => [...prev, newFileObject]);
      }

      // Call unified mutation with category
      // Type assertion needed because mutation accepts all categories, but we only handle ownerChange/other here
      try {
        const data = await addVehicleCardDocument({
          vehicleCardId,
          file,
          category: category as "ownerChange" | "other",
        });

        // Update with actual FileObject from server (replaces temp one)
        if (category === "ownerChange") {
          setCurrentOwnerChange((prev) =>
            prev.map((doc) => (doc.id === newFileObject.id ? data : doc)),
          );
        } else {
          // category must be "other" here
          setCurrentOtherDocuments((prev) =>
            prev.map((doc) => (doc.id === newFileObject.id ? data : doc)),
          );
        }

        toast.success(vehicleCardUILabels(language).documentUploaded, {
          description: vehicleCardUILabels(
            language,
          ).documentUploadedDesc.replace("{name}", file.name),
        });
      } catch (error) {
        // Rollback local state on error
        if (category === "ownerChange") {
          setCurrentOwnerChange((prev) =>
            prev.filter((doc) => doc.id !== newFileObject.id),
          );
        } else {
          setCurrentOtherDocuments((prev) =>
            prev.filter((doc) => doc.id !== newFileObject.id),
          );
        }
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Något gick fel när dokumentet uppladdades";
        toast.error(vehicleCardUILabels(language).uploadFailed, { description: errorMessage });
      }
    }
  };

  const handleDeleteDocument = async (
    documentId: string,
    documentName: string,
  ) => {
    // Store previous state for potential rollback
    const previousPayment = currentPayment;
    const previousOwnerChange = [...currentOwnerChange];
    const previousOtherDocuments = [...currentOtherDocuments];

    // Optimistically remove from local state immediately
    // Check if it's contract or invoice
    if (currentPayment?.contract?.contract_file?.id === documentId) {
      setCurrentPayment((prev) =>
        prev
          ? {
              ...prev,
              contract: prev.contract
                ? {
                    ...prev.contract,
                    contract_file: undefined,
                  }
                : undefined,
            }
          : prev,
      );
    } else if (currentPayment?.invoice?.invoice_file?.id === documentId) {
      setCurrentPayment((prev) =>
        prev
          ? {
              ...prev,
              invoice: prev.invoice
                ? {
                    ...prev.invoice,
                    invoice_file: undefined,
                  }
                : undefined,
            }
          : prev,
      );
    } else {
      // For owner_change or other_documents
      setCurrentOwnerChange((prev) =>
        prev.filter((doc) => doc.id !== documentId),
      );
      setCurrentOtherDocuments((prev) =>
        prev.filter((doc) => doc.id !== documentId),
      );
    }

    // Call the hook to remove document - cache update in onMutate will handle sync
    try {
      await removeDocument({ vehicleCardId, documentId });
      toast.success(vehicleCardUILabels(language).documentRemoved, {
        description: vehicleCardUILabels(language).documentRemovedDesc.replace(
          "{name}",
          documentName,
        ),
      });
    } catch (error) {
      // Rollback local state on error
      setCurrentPayment(previousPayment);
      setCurrentOwnerChange(previousOwnerChange);
      setCurrentOtherDocuments(previousOtherDocuments);
      toast.error(vehicleCardUILabels(language).removalFailed, { description: vehicleCardUILabels(language).documentRemoveError });
    }
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="ownership" className="w-full">
        <TabsList className="w-full justify-start">
          {getCategories(language).map((category) => (
            <TabsTrigger
              key={category.id}
              value={category.id}
              className="flex-1"
            >
              {category.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {getCategories(language).map((category) => (
          <TabsContent
            key={category.id}
            value={category.id}
            className="space-y-6"
          >
            {category.id === "test" ? (
              // Special handling for Test tab with sub-tabs - no upload button for tests
              <Tabs defaultValue="pending" className="w-full">
                <TabsList className="w-full justify-start">
                  <TabsTrigger value="pending" className="flex-1">
                    {vehicleCardUILabels(language).awaitingReview}
                  </TabsTrigger>
                  <TabsTrigger value="approved" className="flex-1">
                    {vehicleCardUILabels(language).approved}
                  </TabsTrigger>
                  <TabsTrigger value="rejected" className="flex-1">
                    {vehicleCardUILabels(language).rejected}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="pending" className="space-y-6">
                  <VehicleCardTestSectionCard
                    testData={testData}
                    statusFilter={(test) =>
                      test.status === "pending-review" ||
                      test.status === "complement"
                    }
                    title={
                      vehicleCardUILabels(language).vehicleTestAwaitingReview
                    }
                    iconColor="text-yellow-600"
                    notFoundMessage={
                      vehicleCardUILabels(language).noUnhandledTests
                    }
                    dateLabel={vehicleCardUILabels(language).date}
                    registrationNumberLabel={
                      vehicleCardUILabels(language).registrationNumberFull
                    }
                    viewTestProtocolTitle={
                      vehicleCardUILabels(language).viewTestProtocol
                    }
                    downloadPdfTitle={vehicleCardUILabels(language).downloadPdf}
                    onViewTest={handleViewTest}
                    onDownloadTestPdf={handleDownloadTestPdf}
                    isTestGeneratingPdf={isTestGeneratingPdf}
                  />
                </TabsContent>

                <TabsContent value="approved" className="space-y-6">
                  <VehicleCardTestSectionCard
                    testData={testData}
                    statusFilter={(test) =>
                      test.status === "approved" ||
                      test.status === "active-listing"
                    }
                    title={vehicleCardUILabels(language).vehicleTestApproved}
                    iconColor="text-green-600"
                    notFoundMessage={
                      vehicleCardUILabels(language).noApprovedTests
                    }
                    dateLabel={vehicleCardUILabels(language).date}
                    registrationNumberLabel={
                      vehicleCardUILabels(language).registrationNumberFull
                    }
                    viewTestProtocolTitle={
                      vehicleCardUILabels(language).viewTestProtocol
                    }
                    downloadPdfTitle={vehicleCardUILabels(language).downloadPdf}
                    onViewTest={handleViewTest}
                    onDownloadTestPdf={handleDownloadTestPdf}
                    isTestGeneratingPdf={isTestGeneratingPdf}
                  />
                </TabsContent>

                <TabsContent value="rejected" className="space-y-6">
                  <VehicleCardTestSectionCard
                    testData={testData}
                    statusFilter={(test) => test.status === "rejected"}
                    title={vehicleCardUILabels(language).vehicleTestRejected}
                    iconColor="text-red-600"
                    notFoundMessage={
                      vehicleCardUILabels(language).noRejectedTests
                    }
                    dateLabel={vehicleCardUILabels(language).date}
                    registrationNumberLabel={
                      vehicleCardUILabels(language).registrationNumberFull
                    }
                    viewTestProtocolTitle={
                      vehicleCardUILabels(language).viewTestProtocol
                    }
                    downloadPdfTitle={vehicleCardUILabels(language).downloadPdf}
                    onViewTest={handleViewTest}
                    onDownloadTestPdf={handleDownloadTestPdf}
                    isTestGeneratingPdf={isTestGeneratingPdf}
                  />
                </TabsContent>
              </Tabs>
            ) : (
              // Regular handling for other tabs
              <>
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="outline"
                    onClick={() => handleUploadDocument(category.id)}
                    disabled={isAddingVehicleCardDocument}
                    className="gap-2"
                  >
                    {isAddingVehicleCardDocument ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {isAddingVehicleCardDocument
                      ? vehicleCardUILabels(language).uploading
                      : vehicleCardUILabels(language).uploadCategory.replace(
                          "{category}",
                          category.label.toLowerCase(),
                        )}
                  </Button>
                </div>

                <FileUploadTrigger
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                  onFilesSelected={(files) => {
                    if (!files[0]) return;
                    const dt = new DataTransfer();
                    dt.items.add(files[0]);
                    void handleFileSelect({
                      target: { files: dt.files },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                >
                  {(open) => {
                    openDocumentPickerRef.current = open;
                    return null;
                  }}
                </FileUploadTrigger>

                <div className="space-y-3">
                  {/* Render contracts - using same design as economy popup */}
                  {category.id === "contract" &&
                    contractData &&
                    contractData.length > 0 && (
                      <div className="relative overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead className="bg-primary/10">
                            <tr>
                              <th className="text-left p-3 font-medium">ID</th>
                              <th className="text-right p-3 font-medium">
                                {vehicleCardUILabels(language).actions}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {contractData.map((contract) => (
                              <tr
                                key={contract.id}
                                className="border-b hover:bg-muted/50"
                              >
                                <td className="p-3 font-medium">
                                  {contract.contract_number}
                                </td>
                                {/* <td className="p-3">
                                  {vehicleCardDetails?.owner?.firstName}{" "}
                                  {vehicleCardDetails?.owner?.lastName}
                                </td> */}
                                <td className="p-3 text-right">
                                  <div className="flex gap-1 justify-end">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        if (contract.contract_file) {
                                          handleDownloadDocument({
                                            id: contract.id,
                                            url: contract.contract_file.url,
                                            name:
                                              contract.contract_file.name ||
                                              "contract.pdf",
                                          });
                                        } else {
                                        }
                                      }}
                                      disabled={!contract.contract_file}
                                      title={
                                        vehicleCardUILabels(language)
                                          .downloadContract
                                      }
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                    {contract.contract_file?.id && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                          handleDeleteDocument(
                                            contract.contract_file!.id!,
                                            contract.contract_file!.name ||
                                              vehicleCardUILabels(
                                                language,
                                              ).contract.toLowerCase(),
                                          );
                                        }}
                                        disabled={isRemovingDocument}
                                        title={
                                          vehicleCardUILabels(language)
                                            .removeContract
                                        }
                                      >
                                        <Trash2 className="h-4 w-4 text-destructive" />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                  {/* Render invoices - using same design as economy popup */}
                  {category.id === "invoice" &&
                    invoiceData &&
                    invoiceData.length > 0 && (
                      <div className="relative overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead className="bg-primary/10">
                            <tr>
                              <th className="text-left p-3 font-medium">ID</th>
                              <th className="text-left p-3 font-medium">
                                {vehicleCardUILabels(language).amount}
                              </th>
                              <th className="text-left p-3 font-medium">
                                {vehicleCardUILabels(language).date}
                              </th>
                              <th className="text-left p-3 font-medium">
                                {vehicleCardUILabels(language).dueDate}
                              </th>
                              <th className="text-left p-3 font-medium">
                                {vehicleCardUILabels(language).status}
                              </th>
                              <th className="text-right p-3 font-medium">
                                {vehicleCardUILabels(language).actions}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoiceData.map((invoice) => (
                              <tr
                                key={invoice.id}
                                className="border-b hover:bg-muted/50"
                              >
                                <td className="p-3 font-medium">
                                  {invoice.invoice_number}
                                </td>
                                <td className="p-3">
                                  {currentPayment?.amount?.toLocaleString()} kr
                                </td>
                                <td className="p-3">
                                  {new Date(
                                    invoice.created_at,
                                  ).toLocaleDateString(
                                    language === "en" ? "en-GB" : "sv-SE",
                                  )}
                                </td>
                                <td className="p-3">
                                  {new Date(
                                    currentPayment.due_date,
                                  ).toLocaleDateString(
                                    language === "en" ? "en-GB" : "sv-SE",
                                  )}
                                </td>
                                <td className="p-3">
                                  <span
                                    className={`px-2 py-1 rounded text-xs ${
                                      currentPayment?.status === "paid"
                                        ? "bg-green-100 text-green-800"
                                        : currentPayment?.status === "unpaid"
                                          ? "bg-gray-100 text-gray-800"
                                          : currentPayment?.status === "overdue"
                                            ? "bg-red-100 text-red-800"
                                            : currentPayment?.status ===
                                                "pending"
                                              ? "bg-yellow-100 text-yellow-800"
                                              : "bg-blue-100 text-blue-800"
                                    }`}
                                  >
                                    {currentPayment?.status === "paid"
                                      ? vehicleCardUILabels(language).paid
                                      : currentPayment?.status === "unpaid"
                                        ? vehicleCardUILabels(language).unpaid
                                        : currentPayment?.status === "overdue"
                                          ? vehicleCardUILabels(language)
                                              .overdue
                                          : currentPayment?.status === "pending"
                                            ? vehicleCardUILabels(language)
                                                .ongoing
                                            : vehicleCardUILabels(language)
                                                .unknown}
                                  </span>
                                </td>
                                <td className="p-3 text-right">
                                  <div className="flex gap-1 justify-end">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        if (invoice.invoice_file) {
                                          handleDownloadDocument({
                                            id: invoice.id,
                                            url: invoice.invoice_file.url,
                                            name:
                                              invoice.invoice_file.name ||
                                              "invoice.pdf",
                                          });
                                        } else {
                                        }
                                      }}
                                      disabled={!invoice.invoice_file}
                                      title={
                                        vehicleCardUILabels(language)
                                          .downloadInvoice
                                      }
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                    {invoice.invoice_file?.id && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                          handleDeleteDocument(
                                            invoice.invoice_file!.id!,
                                            invoice.invoice_file!.name ||
                                              vehicleCardUILabels(
                                                language,
                                              ).invoice.toLowerCase(),
                                          );
                                        }}
                                        disabled={isRemovingDocument}
                                        title={
                                          vehicleCardUILabels(language)
                                            .removeInvoice
                                        }
                                      >
                                        <Trash2 className="h-4 w-4 text-destructive" />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                  {/* Render ownership documents */}
                  {category.id === "ownership" &&
                    currentOwnerChange &&
                    currentOwnerChange.length > 0 &&
                    currentOwnerChange.map((doc) => (
                      <Card key={doc.id} className="shadow-sm">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="h-5 w-5 text-muted-foreground" />
                              <div>
                                <h4 className="font-medium">{doc.name}</h4>
                                <p className="text-sm text-muted-foreground">
                                  {vehicleCardUILabels(language).uploaded}:{" "}
                                  {new Date().toLocaleDateString(
                                    language === "en" ? "en-GB" : "sv-SE",
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleDownloadDocument({
                                    id: doc.id || "",
                                    url: doc.url,
                                    name: doc.name,
                                  })
                                }
                                disabled={!doc.url}
                                title={
                                  vehicleCardUILabels(language).downloadDocument
                                }
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              {doc.id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    handleDeleteDocument(
                                      doc.id!,
                                      doc.name ||
                                        vehicleCardUILabels(
                                          language,
                                        ).otherDocuments.toLowerCase(),
                                    );
                                  }}
                                  disabled={isRemovingDocument}
                                  title={
                                    vehicleCardUILabels(language).removeDocument
                                  }
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                  {/* Render other documents */}
                  {category.id === "other" &&
                    currentOtherDocuments &&
                    currentOtherDocuments.length > 0 &&
                    currentOtherDocuments.map((doc) => (
                      <Card key={doc.id} className="shadow-sm">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="h-5 w-5 text-muted-foreground" />
                              <div>
                                <h4 className="font-medium">{doc.name}</h4>
                                <p className="text-sm text-muted-foreground">
                                  {vehicleCardUILabels(language).uploaded}:{" "}
                                  {new Date().toLocaleDateString(
                                    language === "en" ? "en-GB" : "sv-SE",
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleDownloadDocument({
                                    id: doc.id || "",
                                    url: doc.url,
                                    name: doc.name,
                                  })
                                }
                                disabled={!doc.url}
                                title={
                                  vehicleCardUILabels(language).downloadDocument
                                }
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              {doc.id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    handleDeleteDocument(
                                      doc.id!,
                                      doc.name ||
                                        vehicleCardUILabels(
                                          language,
                                        ).otherDocuments.toLowerCase(),
                                    );
                                  }}
                                  disabled={isRemovingDocument}
                                  title={
                                    vehicleCardUILabels(language).removeDocument
                                  }
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>

                {/* Show empty states */}
                {category.id === "contract" &&
                  (!contractData || contractData.length === 0) && (
                    <div className="text-center py-12 text-muted-foreground">
                      {vehicleCardUILabels(language).noContracts}
                    </div>
                  )}

                {category.id === "invoice" &&
                  (!invoiceData || invoiceData.length === 0) && (
                    <div className="text-center py-12 text-muted-foreground">
                      {vehicleCardUILabels(language).noInvoices}
                    </div>
                  )}

                {category.id === "ownership" &&
                  (!currentOwnerChange || currentOwnerChange.length === 0) && (
                    <div className="text-center py-12 text-muted-foreground">
                      {vehicleCardUILabels(language).noOwnerChangeDocs}
                    </div>
                  )}

                {category.id === "other" &&
                  (!currentOtherDocuments ||
                    currentOtherDocuments.length === 0) && (
                    <div className="text-center py-12 text-muted-foreground">
                      {vehicleCardUILabels(language).noOtherDocs}
                    </div>
                  )}
              </>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Test View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-3xl w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>
              {viewTest
                ? [
                    viewTest.vehicleDetails?.make,
                    viewTest.vehicleDetails?.model,
                    viewTest.vehicleDetails?.year,
                  ]
                    .filter(Boolean)
                    .join(" ") || vehicleCardUILabels(language).testProtocol
                : vehicleCardUILabels(language).testProtocol}
            </DialogTitle>
          </DialogHeader>
          {viewTest && <VehicleTestDocumentView data={viewTest} />}
        </DialogContent>
      </Dialog>

      {/* Invoice Upload Dialog */}
      <InvoiceUploadDialog
        open={invoiceDialogOpen}
        onOpenChange={setInvoiceDialogOpen}
        regNumber={regNumber}
        onSuccess={(createdInvoice) => {
          // Optimistic update: Update local state immediately with the created invoice
          setCurrentPayment((prev) =>
            prev
              ? {
                  ...prev,
                  invoice: createdInvoice,
                }
              : prev,
          );
          toast.success(vehicleCardUILabels(language).invoiceUploadedTitle, { description: vehicleCardUILabels(language).invoiceUploaded });
        }}
      />

      {/* Contract Upload Dialog */}
      <ContractUploadDialog
        open={contractDialogOpen}
        onOpenChange={setContractDialogOpen}
        regNumber={regNumber}
        onSuccess={(createdContract) => {
          // Optimistic update: Update local state immediately with the created contract
          setCurrentPayment((prev) =>
            prev
              ? {
                  ...prev,
                  contract: createdContract,
                }
              : prev,
          );
          toast.success(vehicleCardUILabels(language).contractUploadedTitle, { description: vehicleCardUILabels(language).contractUploaded });
        }}
      />

      {/* PDF Generation Dialog */}
      <PdfGenerationDialog
        isOpen={isGeneratingPdf.isOpen}
        testName={isGeneratingPdf.testName}
        fileName={isGeneratingPdf.fileName}
      />

      {/* Warning Dialog */}
      <Dialog open={warningDialogOpen} onOpenChange={setWarningDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {vehicleCardUILabels(language).multipleDocumentsNotAllowed}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">{warningMessage}</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setWarningDialogOpen(false)}
            >
              {vehicleCardUILabels(language).close}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
